import { createContext, useEffect, useContext, useCallback, useReducer, useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useAuthContext } from '../auth/useAuthContext';

// utils
import axios from '../utils/axios';
import localStorageAvailable from '../utils/localStorageAvailable';

// ----------------------------------------------------------------------

const initialState = {
  businessTags: [],
  error: false,
  page: 0,
  totalPages: 1,
  recordPerPage:15,
  totalRecords:null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      ...state,
      businessTags: action.payload.businessTags,
      page: action.payload.page,
      totalPages: action.payload.totalPages,
      recordPerPage:action.payload.recordPerPage,
      totalRecords: action.payload.totalRecords
    };
  }
  if (action.type === 'ERROR') {
    return {
      ...state,
      error: true,
    };
  }
  if (action.type === 'CREATE_TAG') {
    return {
      ...state,
      businessTags: [...state.businessTags, action.payload.businessTag],
    };
  }
  if (action.type === 'UPDATE_TAG') {
    const tags = [...state.businessTags];
    const index = tags.findIndex((tag) => tag._id === action.payload.businessTag._id);
    tags[index] = action.payload.businessTag;
    return {
      ...state,
      businessTags: tags,
    };
  }
  if (action.type === 'PAGE_CHANGE') {
    return {
      ...state,
      page: action.payload.page,
    };
  }
  if(action.type === 'PAGE_SIZE_CHANGE'){
   return{
    ...state,
    recordPerPage:action.payload.recordPerPage
   }
  }
  return state;
};

// ----------------------------------------------------------------------

export const BusinessTagsContext = createContext(initialState);

export const useBusinessTagsContext = () => {
  const context = useContext(BusinessTagsContext);

  if (!context) throw new Error('useSettingsContext must be use inside SettingsProvider');

  return context;
};

export function BusinessTagsProvider() {
  const { user, logout } = useAuthContext();
  const { enqueueSnackbar } = useSnackbar();

  const storageAvailable = localStorageAvailable();

  const [state, dispatch] = useReducer(reducer, initialState);

  const getBusinessTags = useCallback(
    async (page = 0,pageSize= state.recordPerPage) => {
      try {
        const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
        const config = {
          method: 'get',
          maxBodyLength: Infinity,
          url: '/system/tags',
          headers: {
            authorization: `Bearer ${accessToken}`,
            user,
            'Content-Type': 'application/json',
          },
          params: { page, page_size:pageSize},
        };
        const response = await axios.request(config);
        const { payload, currentPage, totalPages,recordPerPage,totalRecords } = response.data;
        dispatch({
          type: 'INITIAL',
          payload: {
            businessTags: payload,
            page: currentPage,
            totalPages: Math.ceil(totalRecords / pageSize),
            recordPerPage:pageSize,
            totalRecords
          },
        });
        console.log(totalRecords,'ttltt')
      } catch (error) {
        if (error === 'Unauthorized request') {
          logout();
        }
        console.log(error);
        dispatch({
          type: 'ERROR',
        });
      }
    }, [user, storageAvailable, logout]);

  useEffect(() => {
    getBusinessTags();
  }, [getBusinessTags]);

  const pageChange = useCallback(
  (page) => {
    getBusinessTags(page, state.recordPerPage);
  },
  [getBusinessTags, state.recordPerPage]
);

 const setPage = useCallback(
  (updater) => {
    
    const newPage = typeof updater === "function" ? updater(state.page) : updater;

    dispatch({ type: 'PAGE_CHANGE', payload: { page: newPage } });
    getBusinessTags(newPage, state.recordPerPage);
  },
  [getBusinessTags, state.recordPerPage, state.page]
);

const setRecordPerPage = useCallback(
  (recordPerPage) => {
    dispatch({ type: 'PAGE_SIZE_CHANGE', payload: { recordPerPage } });
    getBusinessTags(0, recordPerPage); 
  },
  [getBusinessTags]
);


  const createTag = useCallback(
    async (data) => {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: '/system/tags',
        headers: {
          authorization: `Bearer ${accessToken}`,
          user,
          'Content-Type': 'application/json',
        },
        data,
      };
      const response = await axios.request(config);
      dispatch({
        type: 'CREATE_TAG',
        payload: {
          businessTag: response.data,
        },
      });
    },
    [storageAvailable, user]
  );

  const updateTag = useCallback(
    async (id, data) => {
      const accessToken = storageAvailable ? localStorage.getItem('accessToken') : '';
      const config = {
        method: 'put',
        maxBodyLength: Infinity,
        url: `/system/tags/${id}`,
        headers: {
          authorization: `Bearer ${accessToken}`,
          user,
          'Content-Type': 'application/json',
        },
        data,
      };
      const response = await axios.request(config);
      dispatch({
        type: 'UPDATE_TAG',
        payload: {
          businessTag: response.data,
        },
      });
    },
    [storageAvailable, user]
  );

  const memoizedValue = useMemo(
    () => ({ businessTags: state.businessTags, page: state.page, totalPages: state.totalPages,recordPerPage:state.recordPerPage,totalRecords:state.totalRecords, pageChange, createTag, updateTag,setPage,setRecordPerPage }),
    [createTag, state.businessTags, updateTag, state.page,state.recordPerPage,state.totalRecords, pageChange, setPage,setRecordPerPage,
      state.totalPages,]
  );

  return (
    <BusinessTagsContext.Provider value={memoizedValue}>
      <Outlet />
    </BusinessTagsContext.Provider>
  );
}
